import React, { useEffect, useMemo, useState } from "react";
import { detectMediaType, formatFieldTitle, resolveMediaUrl, sanitizePathForDisplay } from "./contentManagerUtils";

const EditableContentRow = ({
  item,
  t,
  onSaveValue,
  onSaveOrder,
  onToggleActive,
  onUploadMedia,
  onRemoveMedia,
  onDeleteItem,
}) => {
  const [draftValue, setDraftValue] = useState(
    typeof item.value === "string" || typeof item.value === "number" ? String(item.value ?? "") : ""
  );
  const [draftOrder, setDraftOrder] = useState(Number(item.order) || 0);
  const [uploadFile, setUploadFile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);

  useEffect(() => {
    setDraftValue(typeof item.value === "string" || typeof item.value === "number" ? String(item.value ?? "") : "");
    setDraftOrder(Number(item.order) || 0);
    setUploadFile(null);
    setImageFailed(false);
    setVideoFailed(false);
  }, [item.value, item.id, item.path]);

  const mediaSource = useMemo(() => {
    const fromMedia = resolveMediaUrl(item.mediaUrl);
    if (fromMedia) return fromMedia;
    return resolveMediaUrl(draftValue);
  }, [item.mediaUrl, draftValue]);
  const mediaType = detectMediaType(mediaSource);
  const hasUploadsValue = useMemo(() => String(draftValue || "").trim().startsWith("/uploads/"), [draftValue]);

  const displayPath = sanitizePathForDisplay(item.path);
  const fieldLabel = /^\d+$/.test(String(item.label || ""))
    ? formatFieldTitle(item.key || "Field")
    : formatFieldTitle(item.label);

  return (
    <>
      <tr className="border-b border-slate-200 align-top hover:bg-slate-50/70">
        <td className="px-3 py-3 text-sm">
          <p className="font-semibold text-slate-900">{fieldLabel}</p>
          {displayPath ? <p className="text-xs text-slate-500 mt-1 break-all">{displayPath}</p> : null}
          <p className="text-xs text-slate-500 mt-1">
            Type: {item.type || "text"} | Order: {item.order ?? 0}
          </p>
        </td>
        <td className="px-3 py-3 min-w-[420px] text-sm text-slate-700">
          <p className="line-clamp-3 break-words">{draftValue || "-"}</p>
        </td>
        <td className="px-3 py-3 min-w-[260px]">
          {hasUploadsValue && mediaSource ? (
            <div className="rounded-lg border border-slate-200 overflow-hidden bg-slate-50">
              {mediaType === "image" && !imageFailed ? (
                <img src={mediaSource} alt="" className="w-full h-32 object-cover" onError={() => setImageFailed(true)} />
              ) : null}
              {mediaType === "video" && !videoFailed ? (
                <video src={mediaSource} controls className="w-full h-32 object-cover" onError={() => setVideoFailed(true)} />
              ) : null}
              {mediaType === "file" || imageFailed || videoFailed ? (
                <div className="h-32 flex items-center justify-center text-xs text-slate-500 px-2 text-center break-all">{mediaSource}</div>
              ) : null}
            </div>
          ) : (
            <p className="text-xs text-slate-500">Not a media field</p>
          )}
        </td>
        <td className="px-3 py-3 min-w-[120px]">
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="text-sm px-3 py-2 rounded-lg border border-slate-300 hover:bg-slate-50"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => onDeleteItem(item)}
              className="text-sm px-3 py-2 rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50"
            >
              Delete
            </button>
          </div>
        </td>
      </tr>

      {isModalOpen ? (
        <div className="fixed inset-0 z-[12000] bg-slate-900/45 flex items-center justify-center p-4">
          <div className="w-full max-w-4xl max-h-[88vh] overflow-y-auto rounded-2xl bg-white border border-slate-200 shadow-xl">
            <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
              <div>
                <p className="font-semibold text-slate-900">{fieldLabel}</p>
                {displayPath ? <p className="text-xs text-slate-500">{displayPath}</p> : null}
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50"
              >
                Close
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Content Value</label>
                <textarea
                  value={draftValue}
                  onChange={(e) => setDraftValue(e.target.value)}
                  className="w-full min-h-[150px] rounded-lg border border-slate-300 bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => onSaveValue(item, draftValue)}
                    className="text-sm px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                  >
                    {t("content.saveValue")}
                  </button>
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 p-3">
                <p className="text-sm font-semibold text-slate-800 mb-2">Item Settings</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="text-sm text-slate-700">
                    Order
                    <input
                      type="number"
                      value={draftOrder}
                      onChange={(e) => setDraftOrder(Number(e.target.value) || 0)}
                      className="mt-1 w-full rounded-lg border border-slate-300 bg-white p-2 text-sm"
                    />
                  </label>
                  <label className="text-sm text-slate-700 inline-flex items-center gap-2 mt-6">
                    <input
                      type="checkbox"
                      checked={Boolean(item.isActive)}
                      onChange={(e) => onToggleActive(item, e.target.checked)}
                    />
                    Active
                  </label>
                </div>
                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={() => onSaveOrder(item, draftOrder)}
                    className="text-sm px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50"
                  >
                    Save order
                  </button>
                </div>
              </div>

              {hasUploadsValue ? (
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-sm font-semibold text-slate-800 mb-2">Media</p>
                  <div className="space-y-2">
                    <input
                      type="file"
                      onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                      className="w-full text-xs file:mr-2 file:px-2 file:py-1 file:rounded file:border-0 file:bg-slate-200"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => uploadFile && onUploadMedia(item, uploadFile)}
                        className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50"
                      >
                        Upload
                      </button>
                      <button
                        type="button"
                        onClick={() => onRemoveMedia(item)}
                        className="text-xs px-2 py-1 rounded border border-rose-200 text-rose-700 hover:bg-rose-50"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default EditableContentRow;
